import * as Empresas from './data/empresas.js';
import * as Fechamento from './data/fechamento.js';
import * as Operacional from './data/operacional.js';
import * as Documentos from './data/documentos.js';
import * as Comentarios from './data/comentarios.js';

/**
 * Cache em memória do que as telas precisam.
 *
 * O protótipo tinha arrays globais (EMPRESAS, TAREFAS, ...) preenchidos na
 * carga da página. A ideia aqui é a mesma, só que a origem é o Supabase e
 * existe um `recarregar()` — porque agora os dados são compartilhados e
 * podem ter mudado por causa de outro colaborador.
 */

export const dados = {
  colaboradores: [],
  empresas: [],
  resumos: [],        // v_fechamento_resumo
  sinalizados: [],    // itens de conferência não-OK em competências abertas
  tarefas: [],
  pesquisas: [],
  kb: [],
  realizado: [],
  checklist: [],
  checklistTotal: 0,
  competencias: [],   // últimas 7, para a matriz de documentos
  comentariosAbertos: [],   // correções e dúvidas ainda não resolvidas
  docsRecebidos: new Set(),  // banco_id com documento recebido no mês de referência
};

export async function carregarTudo() {
  dados.competencias = Documentos.ultimasCompetencias(7);

  const [
    colaboradores, empresas, resumos, sinalizados,
    tarefas, pesquisas, kb, realizado, checklist, recebidos, comentariosAbertos,
  ] = await Promise.all([
    Empresas.listarColaboradores(),
    Empresas.listar({ incluirEncerrados: true }),
    Fechamento.resumoDeTodos(),
    Fechamento.itensSinalizados(),
    Operacional.listarTarefas(),
    Operacional.listarPesquisas(),
    Operacional.baseConhecimento(),
    Operacional.listarRealizado(),
    Fechamento.checklistPadrao(),
    Documentos.recebidosNoMes(mesDeReferencia()),
    Comentarios.emAberto(),
  ]);

  Object.assign(dados, {
    colaboradores, empresas, resumos, sinalizados,
    tarefas, pesquisas, kb, realizado, checklist,
  });
  dados.docsRecebidos = new Set(recebidos.map((d) => d.banco_id));
  dados.comentariosAbertos = comentariosAbertos;
  dados.checklistTotal = checklist.reduce((n, e) => n + e.itens.length, 0);

  return dados;
}

/** Recarrega só o que muda quando alguém mexe num fechamento. */
export async function recarregarFechamento() {
  const [empresas, resumos, sinalizados] = await Promise.all([
    Empresas.listar({ incluirEncerrados: true }),
    Fechamento.resumoDeTodos(),
    Fechamento.itensSinalizados(),
  ]);
  Object.assign(dados, { empresas, resumos, sinalizados });
}

/* ------------------------------------------------------------------ */
/* Derivações — no protótipo estes valores eram campos digitados       */
/* ------------------------------------------------------------------ */

/**
 * O protótipo guardava `status: 'pendencia' | 'andamento' | 'concluida'`
 * na mão. Agora sai do estado real do fechamento.
 */

// Mesmo vocabulário da empresa, um nível abaixo: o item segue a mesma
// progressão. 'Pendente' virou 'A realizar' porque item que ninguém
// tocou não é pendência — é trabalho que ainda não começou.
export const STATUS_ITEM_ROTULO = { ok: 'OK', parcial: 'Em andamento', pendente: 'A realizar' };
export const STATUS_ITEM_TAG = { ok: 'green', parcial: 'amber', pendente: 'red' };

/** % do checklist da competência aberta da empresa. */
export function progressoEmpresa(empresa) {
  if (!empresa.competencia_aberta) return 100;
  const r = dados.resumos.find(
    (x) => x.empresa_id === empresa.id && x.competencia === empresa.competencia_aberta
  );
  if (!r || !r.checklist_total) return 0;
  return Math.round((r.checklist_feitos / r.checklist_total) * 100);
}

/** Empresas do colaborador que ainda geram trabalho — ex-cliente fica de fora. */
export function empresasDe(colaboradorId) {
  return dados.empresas.filter((e) =>
    e.situacao !== 'encerrado' && !e.matriz_id &&
    (e.responsaveis || []).some((r) => r.id === colaboradorId)
  );
}

/**
 * Empresas que geram trabalho contábil de verdade.
 * Fora: ex-clientes e filiais — na filial o contábil é feito na matriz,
 * então contá-la inflaria os indicadores com fechamento que não existe.
 */
/**
 * As empresas em que alguém de fato trabalha.
 *
 * O painel contava 139 porque só tirava ex-cliente e filial. Faltava tirar
 * as 15 de só-cadastro — doméstica, rural, MEI, Collorparts e Nygata —
 * que existem para a contagem bater com o Domínio e que ninguém atende.
 * Elas apareciam como se houvesse trabalho pendente nelas.
 *
 * Mesma regra que o banco aplica em empresa_gera_fechamento().
 */
export function empresasVigentes() {
  return dados.empresas.filter((e) =>
    e.situacao !== 'encerrado' &&
    !e.matriz_id &&
    e.gera_fechamento !== false &&
    e.validado !== false
  );
}

/**
 * O mês que já deveria estar fechado: o anterior ao corrente.
 *
 * Decisão da gestão. Os meses seguintes continuam abertos para quem
 * antecipa, mas não contam como atraso — senão, com os 12 meses do ano
 * abertos, nenhuma empresa poderia estar em dia.
 */
export function mesDeReferencia(hoje = new Date()) {
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/**
 * Em que pé está cada empresa. Três estados, um por empresa, somando o
 * total — nunca contando itens nem meses.
 *
 * Palavras da gestão: "se eu tenho 170 empresas, eu tenho 50 pendentes e
 * 120 a realizar, ou 20 prontas". A contagem é por empresa.
 *
 * O vocabulário é o dela: concluído, em andamento, a realizar. O painel
 * antigo chamava de "com pendências" o que na verdade era "a realizar" —
 * empresa que ninguém tocou ainda, não empresa com problema.
 */
export const ESTADO_EMPRESA = {
  a_realizar: ['red', 'A realizar'],
  andamento:  ['amber', 'Em andamento'],
  concluido:  ['green', 'Concluído'],
};

export function estadoEmpresa(empresa, referencia = mesDeReferencia()) {
  const aberta = empresa.competencia_aberta;

  // Nada em aberto até o mês que já deveria estar fechado.
  if (!aberta || aberta > referencia) return 'concluido';

  /*
   * "Começou" é ter item de conferência marcado como feito em qualquer
   * competência do ano — não o checklist.
   *
   * A CON001 registrava as cinco frentes (Docs, Folha, Fiscal, Contábil,
   * Conciliação), e foi isso que a importação trouxe. Olhar o checklist
   * aqui dava zero em andamento para todo mundo, inclusive para quem já
   * tinha fechado julho.
   */
  return itensFeitos(empresa) > 0 ? 'andamento' : 'a_realizar';
}

/** Itens de conferência já concluídos, somando todas as competências. */
export function itensFeitos(empresa) {
  return dados.resumos
    .filter((r) => r.empresa_id === empresa.id)
    .reduce((n, r) => n + Math.max(0, r.itens_total - r.itens_pendentes - r.itens_parciais), 0);
}

/** Conta quantas empresas há em cada estado. Sempre soma o total. */
export function contagemPorEstado(lista = empresasVigentes()) {
  const conta = { a_realizar: 0, andamento: 0, concluido: 0, total: lista.length };
  for (const e of lista) conta[estadoEmpresa(e)]++;
  return conta;
}

export function ehFilial(empresa) {
  return !!empresa.matriz_id;
}

export function filiaisDe(empresaId) {
  return dados.empresas.filter((e) => e.matriz_id === empresaId);
}

export function podeEditar(empresa, usuario) {
  if (!usuario) return false;
  if (usuario.papel === 'gestao') return true;
  return (empresa.responsaveis || []).some((r) => r.id === usuario.id);
}
